/**
 * Where a plan's work is about to go, worked out where it can be tested.
 *
 * The owner approves a plan from a screen. Until now that screen showed him what the tasks
 * said and nothing about which computer would run them, so a plan whose every task read "on
 * the MacBook" looked exactly like a plan that was going wherever. It wasn't: the number on
 * the task is the only thing the scheduler reads, and it was absent.
 *
 * This says, in the same order the database decides it, which Worker each task will go to -
 * the Worker the task names, else the Worker the command addresses, else the standing
 * routing scope, else any idle Worker. Nothing here routes anything; it reads the same facts
 * the approval will act on, so the screen and the database cannot disagree about them.
 */

/** One Worker, as the screen knows it: its number, its name, and the computer it is on. */
export interface RoutingWorker {
  readonly number: number;
  readonly name: string;
  readonly nodeName?: string | null;
}

/** Which of the four rules decided a task's Worker. */
export type RouteSource = 'task' | 'command' | 'scope' | 'auto';

export interface TaskRoute {
  /** Null means any idle Worker, which is what Brunel has always done by default. */
  readonly workerNumber: number | null;
  /** What the owner reads: `Worker 2 · MacBook`, or `Any idle Worker`. */
  readonly label: string;
  readonly source: RouteSource;
}

/**
 * Every Worker number a command names as "Worker 9" or "worker #9", in order. The twin of
 * brunel_private.command_named_workers. Naming a Worker is not by itself an assignment; it is
 * what tells a Worker number on a draft apart - one the owner's words name is the owner's.
 */
export function namedWorkerNumbers(text: string): readonly number[] {
  const found = new Set<number>();
  for (const match of text.toLowerCase().matchAll(/(?:^|[^a-z0-9])workers?\s*#?\s*([0-9]{1,4})(?![0-9])/g)) {
    const number = Number.parseInt(match[1] ?? '', 10);
    if (number > 0) found.add(number);
  }
  return [...found].sort((a, b) => a - b);
}

/**
 * The Worker a command opens by addressing: "2 inspect the repo", "Worker 9, ...",
 * "Worker 9: ...", and "Worker 9 should / must / please / to ..." when Worker 9 is the only
 * Worker the command names. A sentence about a Worker ("Worker 9 is offline") is not an
 * address. The twin of brunel_private.leading_worker_number, and it must stay its twin: this
 * one only decides what the owner is shown, the database's decides where work goes.
 */
export function leadingWorkerNumber(text: string): number | null {
  const said = text.trim().toLowerCase();
  const digits = /^([0-9]{1,4})(?:\s|$)/.exec(said)?.[1]
    ?? /^worker\s*#?\s*([0-9]{1,4})\s*[,:]/.exec(said)?.[1]
    ?? (namedWorkerNumbers(said).length === 1
      ? /^worker\s*#?\s*([0-9]{1,4})\s+(?:should|must|please|to)\s+(?!be\s|not\s|never\s|have\s)/.exec(said)?.[1]
      : undefined);
  if (digits === undefined) return null;
  const number = Number.parseInt(digits, 10);
  return number > 0 ? number : null;
}

/** How a Worker is named on screen: its number, and the computer it is on when that is known. */
export function workerLabel(workerNumber: number, workers: readonly RoutingWorker[]): string {
  const worker = workers.find((candidate) => candidate.number === workerNumber);
  if (worker === undefined) return `Worker ${workerNumber}`;
  const machine = worker.nodeName?.trim();
  return machine ? `Worker ${workerNumber} · ${machine}` : `Worker ${workerNumber} · ${worker.name}`;
}

/** One planned task, as much of it as routing cares about. */
export interface RoutableTask {
  readonly preferredWorkerNumber?: number | undefined;
}

/**
 * Where each task of a plan will go, in the database's own order of precedence.
 *
 * An explicit target is never replaced by a weaker one, and a Worker the screen does not
 * recognise is still shown as a Worker: a task pinned to a number nobody has waits, and
 * saying "Worker 7" is how the owner finds that out before approving rather than after.
 */
export function planTaskRoutes(input: {
  readonly tasks: readonly RoutableTask[];
  readonly command: string;
  readonly scopeWorkerNumber: number | null;
  readonly workers: readonly RoutingWorker[];
}): readonly TaskRoute[] {
  const addressed = leadingWorkerNumber(input.command);
  return input.tasks.map((task) => {
    const pinned = typeof task.preferredWorkerNumber === 'number' && task.preferredWorkerNumber > 0
      ? task.preferredWorkerNumber
      : null;
    const source: RouteSource = pinned !== null ? 'task'
      : addressed !== null ? 'command'
        : input.scopeWorkerNumber !== null ? 'scope' : 'auto';
    const number = pinned ?? addressed ?? input.scopeWorkerNumber ?? null;
    return {
      workerNumber: number,
      label: number === null ? 'Any idle Worker' : workerLabel(number, input.workers),
      source,
    };
  });
}

/** True when a plan's tasks do not all go to the same place, so each needs its own line. */
export function routesDiffer(routes: readonly TaskRoute[]): boolean {
  return new Set(routes.map((route) => route.workerNumber)).size > 1;
}

/** What the routing lock says on screen, or null when routing is Auto. */
export function routingLockLabel(
  scopeWorkerNumber: number | null,
  workers: readonly RoutingWorker[],
): string | null {
  if (scopeWorkerNumber === null) return null;
  const worker = workers.find((candidate) => candidate.number === scopeWorkerNumber);
  const machine = worker?.nodeName?.trim();
  return machine ? `${machine} only` : `${workerLabel(scopeWorkerNumber, workers)} only`;
}
